import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Button, Card, Modal, Space, Spin, Typography } from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Actions
import { clear_all_recipes_data, get_all_recipes, recipes_reorder } from '../../../redux/actions/recipesActions';

// Utils
import isEmpty from '../../../utils/isEmpty';

const SortableRecipe = (props) => {
	const { id, children } = props;

	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
		cursor: 'grab',
		marginBottom: '0.5rem'
	};

	return (
		<div ref={setNodeRef} style={style} {...attributes} {...listeners}>
			{children}
		</div>
	);
};

SortableRecipe.propTypes = {
	id: PropTypes.string.isRequired,
	children: PropTypes.node.isRequired
};

const WorldRecipesOrder = (props) => {
	const { messageApi, isModalOpen, setIsModalOpen, world, setWorld } = props;

	const { t } = useTranslation();

	const dispatch = useDispatch();

	const { all_recipes_loading, all_recipes, recipes_reorder_loading } = useSelector(state => state.recipes);

	const [localRecipes, setLocalRecipes] = useState([]);

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor)
	);

	useEffect(() => {
		if (!isEmpty(world)) {
			dispatch(get_all_recipes({ world: world._id.$oid }));
		}
	}, [world]);

	useEffect(() => {
		// Order by the persisted index so the list reflects the current unlock order
		const sorted = [...(all_recipes.recipes || [])].sort(
			(a, b) => (a.index || 0) - (b.index || 0)
		);
		setLocalRecipes(sorted);
	}, [all_recipes.recipes]);

	const handleCancel = () => {
		setIsModalOpen(false);
		setWorld({});
		setLocalRecipes([]);
		dispatch(clear_all_recipes_data());
	}

	const handleDragEnd = (event) => {
		const { active, over } = event;

		if (over && active.id !== over.id) {
			const oldIndex = localRecipes.findIndex(recipe => recipe._id.$oid === active.id);
			const newIndex = localRecipes.findIndex(recipe => recipe._id.$oid === over.id);
			setLocalRecipes(arrayMove(localRecipes, oldIndex, newIndex));
		}
	};

	const onOk = () => {
		const recipesBody = {
			recipes: localRecipes.map((recipe, index) => ({
				recipe: recipe._id.$oid,
				index: index
			}))
		};

		dispatch(recipes_reorder(messageApi, world._id.$oid, recipesBody, handleCancel));
	}

	return (
		<Modal
			title={`${t('worlds.recipesOrder.modalTitle')} — ${world?.name || ''}`}
			centered
			open={isModalOpen}
			onCancel={handleCancel}
			maskClosable={false}
			width={600}
			footer={[
				<Button key='cancel' onClick={handleCancel}>
					{t('buttons.cancel')}
				</Button>,
				<Button
					key='submit'
					type='primary'
					loading={recipes_reorder_loading}
					onClick={onOk}
				>
					{t('buttons.save')}
				</Button>,
			]}
		>
			<Typography.Text type='secondary'>
				{t('worlds.recipesOrder.hint')}
			</Typography.Text>
			<Spin spinning={all_recipes_loading}>
				<div style={{ marginTop: '1rem', maxHeight: '50vh', overflowY: 'auto' }}>
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}
					>
						<SortableContext
							items={localRecipes.map(recipe => recipe._id.$oid)}
							strategy={verticalListSortingStrategy}
						>
							{localRecipes.map((recipe, index) => (
								<SortableRecipe key={recipe._id.$oid} id={recipe._id.$oid}>
									<Card size='small'>
										<Space>
											<HolderOutlined />
											<Typography.Text strong>{index + 1}.</Typography.Text>
											<Typography.Text>{recipe.name}</Typography.Text>
										</Space>
									</Card>
								</SortableRecipe>
							))}
						</SortableContext>
					</DndContext>
				</div>
			</Spin>
		</Modal>
	)
}

WorldRecipesOrder.propTypes = {
	messageApi: PropTypes.object.isRequired,
	isModalOpen: PropTypes.bool.isRequired,
	setIsModalOpen: PropTypes.func.isRequired,
	world: PropTypes.object.isRequired,
	setWorld: PropTypes.func.isRequired
}

export default WorldRecipesOrder;
